// Deep link to a page this person may not see (board system/forbidden--deep-link):
// NO wall and NO 403 screen — the route guard replaces the history entry with
// the person's first allowed page («اليوم» when they may see it) and a toast
// says why (rule 6: what you don't own you don't see). The attempted URL is
// not stored, so Back does not loop.

import { createContext, useContext, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { EmptyState } from '@/components/EmptyState';
import type { Role } from '@/lib/me';
import type { TermKey } from '@/lib/terminology';
import { useToast } from './Toast';
import { labelOf, type DenialReason, type RailEntry } from './gates';

export const HOME_PATH = '/today';

/** Where a refused deep link lands, and the page title the toast names. */
export interface Landing {
  to: string;
  label: string;
}

/**
 * The person's first allowed page: «اليوم» when any visible rail row is it,
 * else the first row of the first rail entry; null when nothing is visible.
 */
export function landingFor(rail: readonly RailEntry[], t: (k: TermKey) => string = (k) => k): Landing | null {
  for (const e of rail) {
    for (const item of e.menu) {
      if (item.to.split('?')[0] === HOME_PATH) return { to: HOME_PATH, label: labelOf(item, t) };
    }
  }
  const first = rail[0]?.menu[0];
  return first ? { to: first.to, label: labelOf(first, t) } : null;
}

/**
 * The shell's landing (AppShell provides it from the visible rail) so a module
 * that renders <Forbidden/> itself lands in the same place. undefined = no
 * shell around (tests) → «اليوم».
 */
export const LandingContext = createContext<Landing | null | undefined>(undefined);

const ROLE_COPY: Record<Role, string> = {
  staff: 'هذه الصفحة غير متاحة لك.',
  manager: 'هذه الصفحة للمدير فما فوق.',
  owner: 'هذه الصفحة للمالك فقط.',
};
/** Flag-off / plan-gated / wrong operating mode (spec A8 §7, proposed copy). */
const FEATURE_COPY = 'هذه الميزة غير مفعّلة لنشاطك.';
const PERSONAL_COPY = 'هذه الصفحة غير متاحة لك.';

/** The first sentence: why. */
export function forbiddenReason(reason: DenialReason, minRole?: Role): string {
  if (reason === 'role' && minRole) return ROLE_COPY[minRole];
  if (reason === 'capability') return PERSONAL_COPY;
  return FEATURE_COPY;
}

/** The toast: why + where they landed («… عدت إلى «اليوم».»). */
export function forbiddenMessage(reason: DenialReason, minRole?: Role, landingLabel = 'اليوم'): string {
  return `${forbiddenReason(reason, minRole)} عدت إلى «${landingLabel}».`;
}

export function Forbidden({ reason, minRole, landing }: { reason: DenialReason; minRole?: Role; landing?: Landing | null }) {
  const { toast } = useToast();
  const { pathname } = useLocation();
  const shown = useRef(false);
  const fromShell = useContext(LandingContext);
  const chosen = landing !== undefined ? landing : fromShell;
  const target = chosen === undefined ? { to: HOME_PATH, label: 'اليوم' } : chosen;
  const stuck = !target || target.to.split('?')[0] === pathname;
  // The app's own default page being refused (e.g. erp.core off) is not a
  // deep link the person followed: move on silently.
  const silent = pathname === HOME_PATH;
  useEffect(() => {
    if (shown.current || stuck || silent || !target) return;
    shown.current = true;
    toast(forbiddenMessage(reason, minRole, target.label), { duration: 5000 });
  }, [reason, minRole, toast, stuck, silent, target]);
  // Nowhere allowed to go back to: say so in place (no redirect loop).
  if (stuck) return <EmptyState title={forbiddenReason(reason, minRole)} data-testid="forbidden-home" />;
  return <Navigate to={target.to} replace />;
}
